import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotify } from "react-admin";
import { ResourceName } from "@/types";

/**
 * Deferred recompute refresh (#5896, companion to BE #5895).
 *
 * A laser / deductible edit publishes an async recompute (#5769 is silent and
 * eventually consistent). A naive "invalidate on save" refetches BEFORE the
 * worker finishes and re-renders the same stale snapshot. Instead we re-pull the
 * affected queries on a small, FIXED schedule so the recomputed values land on
 * their own, and expose an `isRecomputing` flag so stale tiles can show an
 * "updating…" cue rather than reading as final.
 *
 * The schedule is finite and capped by construction — there is NO unbounded
 * polling. If the worker hasn't finished by the last attempt (or the BE trigger
 * isn't deployed yet), the flag clears and the UI simply shows the current
 * snapshot — i.e. it degrades to today's behavior.
 */
export const RECOMPUTE_REFRESH_DELAYS_MS = [1500, 4000, 8000] as const;

const RECOMPUTE_REFRESH_MESSAGE = "Saved. Claim totals and stop-loss will refresh shortly.";

// Query-key prefixes to invalidate. React-admin keys queries by resource name,
// so a prefix invalidation refetches every getOne/getList for that resource —
// the claimant record (spend / effective deductible tiles), the policy record
// and the stop-loss claims list/summary.
// The conditional-laser and contract-laser keys (Feature #3502, #5287) are react-query keys of their own
// (httpClient hooks), so they are listed explicitly: a resolve or a condition
// edit must clear the review flag and re-derive trigger state without a reload.
//
// The policy's Lasered Claimants list is NOT covered by ["policies"]: it is a
// nested react-admin resource keyed by the whole path
// ("policies/{id}/lasered-claimants"), so it is matched separately below.
const affectedQueryKeys = [
  [ResourceName.CLAIMANTS],
  [ResourceName.POLICIES],
  [ResourceName.STOPLOSS_CLAIMS],
  ["lasered-conditions"],
  // A laser save or remove can set or clear a contract laser (PO 2026-10-07: a laser type of its own).
  ["contract-laser"],
  ["contract-lasers-summary"],
  ["deductible-reviews"],
  ["deductible-resolution-events"],
];

/** react-admin queries of the nested `policies/{id}/lasered-claimants` resource. */
export const isLaseredClaimantsListQuery = (query: { queryKey: readonly unknown[] }): boolean =>
  typeof query.queryKey[0] === "string" && /^policies\/[^/]+\/lasered-claimants$/.test(query.queryKey[0]);

export interface RecomputeRefreshState {
  /** True while the deferred refetch window is open — drives the "updating…" cue. */
  isRecomputing: boolean;
  /**
   * Call on a successful laser/deductible save to start the deferred refresh.
   * Pass `{ notifyUser: false }` when the surface already shows its own
   * refresh-spirit copy (e.g. the lasered-claimant dialog) to avoid a double toast.
   */
  triggerRecomputeRefresh: (opts?: { notifyUser?: boolean }) => void;
}

/**
 * Standalone deferred-refresh hook. Used directly by surfaces outside the
 * claimant page (e.g. the policy-page LaseredClaimantDialog) and internally by
 * {@link RecomputeRefreshProvider} for claimant-page surfaces + tiles.
 */
export const useDeferredRecomputeRefresh = (): RecomputeRefreshState => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [isRecomputing, setIsRecomputing] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  // Cancel any pending refetches if the component unmounts mid-window.
  useEffect(() => clearTimers, [clearTimers]);

  const triggerRecomputeRefresh = useCallback(
    (opts?: { notifyUser?: boolean }) => {
      if (opts?.notifyUser !== false) {
        notify(RECOMPUTE_REFRESH_MESSAGE, { type: "info" });
      }
      clearTimers();
      setIsRecomputing(true);

      const invalidate = () => {
        affectedQueryKeys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
        void queryClient.invalidateQueries({ predicate: isLaseredClaimantsListQuery });
      };

      RECOMPUTE_REFRESH_DELAYS_MS.forEach((delay, i) => {
        const isLast = i === RECOMPUTE_REFRESH_DELAYS_MS.length - 1;
        timers.current.push(
          setTimeout(() => {
            invalidate();
            if (isLast) setIsRecomputing(false);
          }, delay)
        );
      });
    },
    [notify, clearTimers, queryClient]
  );

  return { isRecomputing, triggerRecomputeRefresh };
};

/**
 * `isProvided` distinguishes a real provider from the inert default, so a surface
 * that can render EITHER under the claimant provider (tiles present) OR standalone
 * (e.g. the policy page) can decide whether the context will actually drive the
 * tiles. The default's trigger is a no-op, so a surface must NOT rely on it to
 * refetch — it falls back to its own {@link useDeferredRecomputeRefresh} instance.
 */
type RecomputeRefreshContextValue = RecomputeRefreshState & { isProvided: boolean };

const RecomputeRefreshContext = createContext<RecomputeRefreshContextValue>({
  isRecomputing: false,
  triggerRecomputeRefresh: () => {},
  isProvided: false,
});

/**
 * Shares one deferred-refresh instance across a claimant page so the save
 * surfaces (contract laser, lasered-claimant dialog) can trigger it and the tiles
 * can read `isRecomputing`.
 */
export const RecomputeRefreshProvider = ({ children }: { children: ReactNode }) => {
  const value = useDeferredRecomputeRefresh();
  return (
    <RecomputeRefreshContext.Provider value={{ ...value, isProvided: true }}>
      {children}
    </RecomputeRefreshContext.Provider>
  );
};

export const useRecomputeRefresh = (): RecomputeRefreshContextValue => useContext(RecomputeRefreshContext);

/**
 * The page's shared refresh when a {@link RecomputeRefreshProvider} is mounted
 * (claimant page: the tiles show "updating…"), otherwise a standalone instance
 * owned by the caller. Call it from a component that stays mounted for the whole
 * refresh window: the standalone timers are cancelled when their owner unmounts.
 */
export const useRecomputeRefreshOrStandalone = (): RecomputeRefreshState => {
  const context = useRecomputeRefresh();
  const standalone = useDeferredRecomputeRefresh();
  return context.isProvided ? context : standalone;
};
